/* eslint-disable @typescript-eslint/adjacent-overload-signatures */
import { Injectable, PipeTransform } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { IUser } from '../interfaces/iuser';
import { DecimalPipe } from '@angular/common';
import { debounceTime, delay, switchMap, take, tap } from 'rxjs/operators';
import { SortColumn, SortDirection } from '../directives/sortable.directive';
import { MailingListService } from './mailing-list.service';
import { SearchResult } from '@core/interfaces/itable-column';
import { State } from '@core/interfaces/itable-column';

const compare = (v1: string | number, v2: string | number) => (v1 < v2 ? -1 : v1 > v2 ? 1 : 0);

function sort(records: any[], column: SortColumn, direction: string): any[] {
	if (direction === '' || column === '') {
		return records;
	} else {
		return [...records].sort((a, b) => {
			const res = compare(a[column], b[column]);
			return direction === 'asc' ? res : -res;
		});
	}
}

function matches<Type extends IUser>(record: Type, term: string, pipe: PipeTransform) {
	const values = Object.values(record);
	return (
		values.find(element => element.toString().toLowerCase().includes(term.toString().toLowerCase()))

	);
}
@Injectable({ providedIn: 'root' })
export class TableSubscriber {
	tableData!: any;

	private _loading$ = new BehaviorSubject<boolean>(true);
	private _search$ = new Subject<void>();
	private _records$ = new BehaviorSubject<IUser[]>([]);
	private _total$ = new BehaviorSubject<number>(0);

	private _state: State = {
		page: 1,
		pageSize: 10,
		searchTerm: '',
		sortColumn: '',
		sortDirection: '',
	};

	defaultSearchPayload = {
		PageIndex: 1,
		ItemsPerPage: 1000, // @todo: Racca et al: this should not be hardcoded
		SearchTerm: ''
	}

	constructor(
		private pipe: DecimalPipe,
		private mailingList: MailingListService
	) {
		// @todo - needs to be handled before this happens
		this.mailingList.search(this.defaultSearchPayload).pipe(take(1)).subscribe((result) => {
			this.tableData = result;

			this._search$
				.pipe(
					tap(() => this._loading$.next(true)),
					debounceTime(200),
					switchMap(() => this._search()),
					delay(200),
					tap(() => this._loading$.next(false)),
				)
				.subscribe((result) => {
					this._records$.next(result.records);
					this._total$.next(result.total);
				});

			this._search$.next();
		});
	}

	get records$() {
		return this._records$.asObservable();
	}
	get total$() {
		return this._total$.asObservable();
	}
	get loading$() {
		return this._loading$.asObservable();
	}
	get page() {
		return this._state.page;
	}
	get pageSize() {
		return this._state.pageSize;
	}
	get searchTerm() {
		return this._state.searchTerm;
	}

	set page(page: number) {
		this._set({ page });
	}
	set pageSize(pageSize: number) {
		this._set({ pageSize });
	}
	set searchTerm(searchTerm: string) {
		this._set({ searchTerm });
	}
	set sortColumn(sortColumn: SortColumn) {
		this._set({ sortColumn });
	}
	set sortDirection(sortDirection: SortDirection) {
		this._set({ sortDirection });
	}

	private _set(patch: Partial<State>) {
		Object.assign(this._state, patch);
		this._search$.next();
	}

	private _search(): Observable<SearchResult> {
		const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._state;

		// 1. sort
		let records = sort(this.tableData, sortColumn, sortDirection);

		// 2. filter
		records = records.filter((record) => matches<IUser>(record, searchTerm, this.pipe));
		const total = records.length;

		// 3. paginate
		records = records.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
		return of({ records, total });
	}
}
